import { useState } from "react";
import { motion } from "framer-motion";
import { Edit2, Save, X, Info, ShieldCheck } from "lucide-react";
import { toast } from "react-hot-toast";
import CategoryModal from "../CategoryModal"; // Assuming in components root
import { CATEGORY_DESCRIPTIONS, CATEGORIES } from "../../shared/categories";
import DiaryHistory from "./DiaryHistory";

import api from "../../api/axios";

export default function SeekerProfile({ data, refreshProfile }) {
    const [isEditing, setIsEditing] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);

    // Seeker specific data is nested in data.seeker
    // User common data is in root (name, email)
    // DOB is usually on User

    // Form State
    const [form, setForm] = useState({
        name: data.name,
        dob: data.seeker?.user?.dob || "",
        guardianConsent: data.seeker?.guardianConsent || false,
        isAnonymous: data.seeker?.isAnonymous || false // Ghost Mode
    });

    const handleUpdateDetails = async () => {
        try {
            await api.put("/profile/details", {
                name: form.name,
                guardianConsent: form.guardianConsent,
                isAnonymous: form.isAnonymous
            });

            toast.success("Personal details updated successfully");
            setIsEditing(false);
            refreshProfile();
        } catch (err) {
            toast.error("Failed to update details");
        }
    };

    const handleCategoryUpdate = async (selectedIds) => {
        try {
            await api.put("/profile/categories", {
                categoryIds: selectedIds
            });

            toast.success("Categories updated successfully");
            setShowCategoryModal(false);
            refreshProfile();
        } catch (err) {
            toast.error("Failed to update categories");
        }
    };

    // Helper to get labels
    const getCategoryLabel = (id) => CATEGORIES.find(c => c.id === id)?.label || id;

    return (
        <div className="space-y-6">
            {/* 1. Personal Details */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Personal Details</h2>
                    {!isEditing ? (
                        <button
                            onClick={() => setIsEditing(true)}
                            className="p-2 text-brand-blue hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        >
                            <Edit2 className="w-5 h-5" />
                        </button>
                    ) : (
                        <div className="flex gap-2">
                            <button
                                onClick={() => setIsEditing(false)}
                                className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                            >
                                <X className="w-5 h-5" />
                            </button>
                            <button
                                onClick={handleUpdateDetails}
                                className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                            >
                                <Save className="w-5 h-5" />
                            </button>
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Username (Anonymous)</label>
                        <div className="text-gray-900 dark:text-gray-200 font-mono bg-gray-50 dark:bg-gray-900 p-2 rounded">
                            {data.seeker?.username || "N/A"}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Username cannot be changed.</p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Email</label>
                        <div className="text-gray-900 dark:text-gray-200 bg-gray-50 dark:bg-gray-900 p-2 rounded">
                            {data.email}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">Email cannot be changed.</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Name</label>
                        {isEditing ? (
                            <input
                                type="text"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                className="w-full p-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        ) : (
                            <div className="text-gray-900 dark:text-gray-200 p-2">{data.name}</div>
                        )}
                    </div>

                    {isEditing && (
                        <div className="col-span-1 md:col-span-2 bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <div className="relative">
                                    <input
                                        type="checkbox"
                                        className="sr-only peer"
                                        checked={form.isAnonymous}
                                        onChange={e => setForm(old => ({ ...old, isAnonymous: e.target.checked }))}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 dark:peer-focus:ring-indigo-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-indigo-600"></div>
                                </div>
                                <div>
                                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                        <ShieldCheck className="w-4 h-4 text-brand-blue" /> Private Identity
                                    </span>
                                    <p className="text-xs text-gray-500">
                                        When enabled, your name and avatar are hidden from providers. You will appear as "Anonymous Seeker".
                                    </p>
                                </div>
                            </label>
                        </div>
                    )}

                    {!isEditing && form.isAnonymous && (
                        <div className="col-span-1 md:col-span-2 bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg flex items-center gap-2 border border-indigo-100">
                            <ShieldCheck className="w-5 h-5 text-indigo-600" />
                            <span className="text-sm text-indigo-700 dark:text-indigo-300 font-medium">Private Identity Active: You are anonymous to providers.</span>
                        </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-500 mb-1">Age</label>
                        <div className="text-gray-900 dark:text-gray-200 p-2">{data.seeker?.age}</div>
                    </div>
                </div>
            </section>

            {/* 2. My Care Needs (Categories) */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">My Care Needs</h2>
                    <button
                        onClick={() => setShowCategoryModal(true)}
                        className="text-sm px-4 py-2 bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/20 rounded-full font-medium transition-colors"
                    >
                        Edit Categories
                    </button>
                </div>

                <div className="flex flex-wrap gap-2">
                    {data.seeker?.categories?.length > 0 ? (
                        data.seeker.categories.map((sc) => {
                            const catId = sc.category?.id || sc.categoryId; // Handle nested or flat
                            const rawLabel = sc.category?.label || getCategoryLabel(catId);
                            const catLabel = rawLabel.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
                            return (
                                <span key={catId} className="px-3 py-1 bg-brand-blue/10 text-brand-blue rounded-full text-sm font-medium">
                                    {catLabel}
                                </span>
                            );
                        })
                    ) : (
                        <p className="text-gray-500 italic">No categories selected.</p>
                    )}
                </div>

                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-xl flex gap-3">
                    <Info className="w-5 h-5 text-blue-500 flex-shrink-0" />
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                        Updating these categories helps us match you with the right providers. For "High Risk" needs, we ensure you only see certified professionals.
                    </p>
                </div>
            </section>

            {/* 3. Diary History */}
            <section className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-100 dark:border-gray-700">
                <DiaryHistory limit={2} />
            </section>

            {/* Category Modal */}
            {showCategoryModal && (
                <CategoryModal
                    isOpen={true}
                    onClose={() => setShowCategoryModal(false)}
                    onSelect={handleCategoryUpdate}
                    initialSelected={data.seeker?.categories?.map(c => c.categoryId) || []}
                    hasHighRiskNeeds={false} // Seeker is defining needs, not checking against them here per se
                    isProviderView={false}
                />
            )}
        </div>
    );
}
